import {useEffect, useCallback, useRef} from 'react'
import gql from 'graphql-tag'
import useWebsiteId from './useWebsiteId'
import {useMutate} from '@packages/apollo-hooks'
import useUserId from './useUserId'

const trackedEvents = ['keydown', 'scroll', 'mouseover']

export default function useActiveSessionNotifier() {
  const websiteId = useWebsiteId()
  const mutate = useMutate()
  const interacted = useRef(false)
  const userId = useUserId()

  const notifyActive = useCallback(async () => {
    if (!userId || !websiteId) return

    try {
      await mutate({
        mutation: gql`
          mutation createOrUpdateActiveSession($websiteId: ID) {
            createOrUpdateActiveSession(websiteId: $websiteId) {
              result
            }
          }
        `,
        variables: {websiteId}
      })
    } catch (error) {
      console.log('Failed to track session activity.')
    }
  }, [websiteId, userId])

  const handleInteraction = () => {
    interacted.current = true
    unloadEventListeners()
  }

  const loadEventListeners = () =>
    trackedEvents.forEach(e => addEventListener(e, handleInteraction))

  const unloadEventListeners = () =>
    trackedEvents.forEach(e => removeEventListener(e, handleInteraction))

  useEffect(() => {
    loadEventListeners()
    ;(async () => await notifyActive())()

    const interval = setInterval(async () => {
      if (interacted.current) {
        await notifyActive()
        interacted.current = false
        loadEventListeners()
      }
      // notify every minute
    }, 1000 * 60)

    return () => {
      unloadEventListeners()
      clearInterval(interval)
    }
  }, [notifyActive])
}
